import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import ChatbotWidget from '../../components/ui/ChatbotWidget';
import CalendarHeader from './components/CalendarHeader';
import CalendarFilters from './components/CalendarFilters';
import CalendarGrid from './components/CalendarGrid';
import UpcomingDeadlines from './components/UpcomingDeadlines';
import EventDetailsModal from './components/EventDetailsModal';
import NotificationSettings from './components/NotificationSettings';
import Icon from '../../components/AppIcon';
import { getDeadlines } from '../../services/deadlineService';

const AdmissionCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const [filters, setFilters] = useState({
    institutionType: 'all',
    programLevel: 'all',
    region: 'all',
    eventTypes: ['applications', 'exams', 'results', 'holiday', 'academic'],
    favoritesOnly: false
  });

  // State for scraped deadlines from Supabase
  const [scrapedDeadlines, setScrapedDeadlines] = useState([]);
  const [loadingDeadlines, setLoadingDeadlines] = useState(true);

  // Load deadlines from Supabase on component mount
  useEffect(() => {
    loadScrapedDeadlines();
  }, []);

  const loadScrapedDeadlines = async () => {
    try {
      setLoadingDeadlines(true);
      const deadlines = await getDeadlines({ futureOnly: false, limit: 100 });

      // Transform Supabase deadlines to match calendar event format
      const transformedDeadlines = deadlines.map(deadline => ({
        id: `scraped-${deadline.id}`,
        title: deadline.school_name,
        institution: deadline.school_name,
        institutionId: deadline.id,
        type: mapEventType(deadline.event_type),
        typeLabel: deadline.event_type,
        date: deadline.deadline_date || new Date().toISOString().split('T')[0],
        location: 'Voir article',
        program: deadline.article_title,
        description: deadline.article_title,
        isEstimated: false,
        applicationUrl: deadline.article_url,
        isScraped: true, // Flag to identify scraped events
        source: deadline.source
      }));

      setScrapedDeadlines(transformedDeadlines);
    } catch (error) {
      console.error('Error loading deadlines:', error);
    } finally {
      setLoadingDeadlines(false);
    }
  };

  // Map event types from scraped data to calendar types
  const mapEventType = (eventType) => {
    const typeMap = {
      'concours': 'exams',
      'bourse': 'applications',
      'inscription': 'applications',
      'formation': 'academic',
      'résultats': 'results',
      'information': 'academic',
      'orientation': 'academic'
    };
    return typeMap[eventType] || 'academic';
  };

  // --- DONNÉES DU CALENDRIER (Maroc 2025) ---
  const currentYear = new Date().getFullYear();

  const moroccanEvents = [
    // --- VACANCES & FÉRIÉS (Dates fixes & Religieuses approx) ---
    {
      id: 'h1',
      title: "Manifeste de l'Indépendance",
      type: "holiday",
      typeLabel: "Jour Férié",
      date: `${currentYear}-01-11`,
      location: "National",
      description: "Commémoration de la présentation du Manifeste de l'Indépendance.",
      isEstimated: false
    },
    {
      id: 'h2',
      title: "Vacances du Printemps",
      type: "holiday",
      typeLabel: "Vacances Scolaires",
      date: `${currentYear}-03-16`, // Date approximative selon calendrier scolaire
      location: "National",
      description: "Vacances scolaires intermédiaires (8 jours).",
      isEstimated: false
    },
    {
      id: 'h3',
      title: "Aïd Al-Fitr (Fin du Ramadan)",
      type: "holiday",
      typeLabel: "Fête Religieuse",
      date: `${currentYear}-03-31`,
      location: "National",
      description: "Date sujette à l'observation lunaire. Fin du mois sacré de Ramadan.",
      isEstimated: true
    },
    {
      id: 'h4',
      title: "Fête du Travail",
      type: "holiday",
      typeLabel: "Jour Férié",
      date: `${currentYear}-05-01`,
      location: "National",
      description: "Fête du travail au Maroc.",
      isEstimated: false
    },
    {
      id: 'h5',
      title: "Aïd Al-Adha",
      type: "holiday",
      typeLabel: "Fête Religieuse",
      date: `${currentYear}-06-06`,
      location: "National",
      description: "Date sujette à l'observation lunaire. Fête du sacrifice.",
      isEstimated: true
    },

    // --- ÉVÉNEMENTS ACADÉMIQUES ---
    {
      id: 'ac1',
      title: "Baccalauréat (Examen National)",
      type: "exams",
      typeLabel: "Examen National",
      date: `${currentYear}-06-10`,
      location: "Lycées (National)",
      description: "Épreuves de l'examen national unifié du Baccalauréat (Session Ordinaire).",
      isEstimated: true
    },
    {
      id: 'ac2',
      title: "Rentrée Universitaire 2025-2026",
      type: "academic",
      typeLabel: "Rentrée",
      date: `${currentYear}-09-15`, // Souvent mi-septembre
      location: "Universités",
      description: "Début officiel des cours dans les universités et grandes écoles marocaines.",
      isEstimated: true
    },

    // --- CONCOURS & ADMISSIONS (Mix de dates réelles et estimées) ---
    {
      id: 1,
      title: "Candidature ENSAM (Prévisionnel)",
      institution: "École Nationale Supérieure d'Arts et Métiers",
      institutionId: "ensam",
      type: "applications",
      typeLabel: "Candidature",
      date: `${currentYear}-06-15`, // Basé sur l'année précédente
      location: "En ligne",
      program: "Cycle Ingénieur",
      availableSpots: "Variable",
      description: "⚠️ DATE BASÉE SUR L'ANNÉE PRÉCÉDENTE.\n\nOuverture de la plateforme 'Tawjihi' pour les candidatures aux ENSAM (Casablanca, Meknès, Rabat...).",
      requirements: ["Bac Scientifique ou Technique", "Inscription sur Tawjihi"],
      isEstimated: true,
      applicationUrl: "https://www.tawjihi.ma"
    },
    {
      id: 2,
      title: "Concours Commun Médecine (FMP/FMD)",
      institution: "Facultés de Médecine, Pharmacie et Dentaire",
      institutionId: "fmp",
      type: "exams",
      typeLabel: "Concours",
      date: `${currentYear}-07-20`, // Souvent après le bac
      location: "National",
      program: "Médecine Générale",
      description: "⚠️ DATE ESTIMÉE.\n\nConcours commun d'accès aux études de médecine. Épreuves : SVT, Physique, Chimie, Maths.",
      isEstimated: true,
      requirements: ["Bac avec mention", "Présélection sur note"],
      documents: ["Convocation", "CNI"]
    },
    {
      id: 3,
      title: "CNC (Concours National Commun) - Écrit",
      institution: "CPGE (Classes Prépas)",
      institutionId: "cnc",
      type: "exams",
      typeLabel: "Concours CPGE",
      date: `${currentYear}-05-20`,
      location: "Centres d'examen CPGE",
      program: "Grandes Écoles d'Ingénieurs",
      description: "Épreuves écrites du CNC pour les élèves des classes préparatoires (MPSI, PCSI, TSI).",
      isEstimated: false // Dates souvent fixées tôt
    },
    {
      id: 4,
      title: "TAFEM (Concours ENCG)",
      institution: "Réseau ENCG Maroc",
      institutionId: "encg",
      type: "exams",
      typeLabel: "Concours",
      date: `${currentYear}-07-15`,
      location: "National",
      program: "Commerce et Gestion",
      description: "⚠️ DATE PRÉVISIONNELLE.\n\nTest d'Admissibilité à la Formation en Management (TAFEM) pour l'accès aux ENCG.",
      isEstimated: true,
      applicationUrl: "https://tafem.ma"
    },
    {
      id: 5,
      title: "Date Limite Inscription UM6P",
      institution: "Université Mohammed VI Polytechnique",
      institutionId: "um6p",
      type: "applications",
      typeLabel: "Deadline",
      date: `${currentYear}-05-31`,
      location: "Ben Guerir / Rabat",
      program: "Licences & Ingénierie",
      description: "Dernier délai pour soumettre les dossiers de candidature pour la première session d'admission.",
      isEstimated: false,
      applicationUrl: "https://um6p.ma"
    }
  ];

  const handleNavigate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(currentDate.getMonth() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'week') {
      newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));
    }
    setCurrentDate(newDate);
  };

  const handleTodayClick = () => {
    setCurrentDate(new Date());
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setIsEventModalOpen(true);
  };

  const handleCloseEventModal = () => {
    setIsEventModalOpen(false);
    setSelectedEvent(null);
  };

  // Logique de filtrage - combine hardcoded events with scraped deadlines
  const allEvents = [...moroccanEvents, ...scrapedDeadlines];

  const filteredEvents = allEvents.filter(event => {
    // 1. Filtrer par type d'événement
    if (filters.eventTypes.length > 0 && !filters.eventTypes.includes(event.type)) {
      return false;
    }
    // 2. Filtrer les jours fériés si on ne veut voir que l'académique (optionnel)
    // Ici on affiche tout pour l'instant
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <BreadcrumbNavigation />

        {/* Loading and Refresh Controls */}
        <div className="flex items-center justify-between mb-4 bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center gap-2">
            {loadingDeadlines && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Chargement des échéances...</span>
              </div>
            )}
            {!loadingDeadlines && scrapedDeadlines.length > 0 && (
              <div className="text-sm text-green-600 flex items-center gap-2">
                <Icon name="CheckCircle" size={16} />
                <span>{scrapedDeadlines.length} échéances en ligne</span>
              </div>
            )}
          </div>
          <button
            onClick={loadScrapedDeadlines}
            disabled={loadingDeadlines}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
          >
            <Icon name="RefreshCw" size={14} className={loadingDeadlines ? 'animate-spin' : ''} />
            <span>Actualiser</span>
          </button>
        </div>

        <CalendarHeader
          currentDate={currentDate}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onNavigate={handleNavigate}
          onTodayClick={handleTodayClick}
        />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <CalendarFilters
              filters={filters}
              onFiltersChange={setFilters}
              isExpanded={isFiltersExpanded}
              onToggleExpanded={() => setIsFiltersExpanded(!isFiltersExpanded)}
            />

            <div className="mt-6">
              <UpcomingDeadlines
                events={filteredEvents}
                onEventClick={handleEventClick}
              />
            </div>

            {/* Légende rapide */}
            <div className="mt-6 bg-card p-4 rounded-lg border border-border shadow-sm">
              <h4 className="font-semibold text-sm mb-3">Légende</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <span className="w-3 h-3 rounded-full bg-red-500 mr-2"></span>
                  <span>Fériés / Vacances</span>
                </div>
                <div className="flex items-center">
                  <span className="w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
                  <span>Concours / Examens</span>
                </div>
                <div className="flex items-center">
                  <span className="w-3 h-3 rounded-full bg-amber-500 mr-2"></span>
                  <span>Candidatures</span>
                </div>
                <div className="flex items-center text-muted-foreground text-xs mt-2 italic">
                  <Icon name="AlertCircle" size={12} className="mr-1" />
                  Dates prévisionnelles marquées
                </div>
              </div>
            </div>
          </div>

          {/* Main Calendar */}
          <div className="lg:col-span-3">
            <CalendarGrid
              currentDate={currentDate}
              events={filteredEvents}
              onEventClick={handleEventClick}
              viewMode={viewMode}
            />

          </div>
        </div>
      </main>

      {/* Modals */}
      <EventDetailsModal
        event={selectedEvent}
        isOpen={isEventModalOpen}
        onClose={handleCloseEventModal}
      />

      <NotificationSettings
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
      />

      <ChatbotWidget />
    </div>
  );
};

export default AdmissionCalendar;